/**
 * `reports.*` — read-only aggregations over invoices + receipts + CNs.
 *
 * V1.0.x procedures (see `domain/sales/reports/index.ts`):
 *   - `aging` — bucket roll-up + per-customer
 *   - `customerStatement` — date-range printable ledger
 *   - `customerConcentration` — share-of-receivables (founder)
 *   - `invoiceActivityFeed` — per-invoice audit trail
 */
import { z } from "zod";
import { uuid, isoDate, moneyPaise, cursor } from "../_common.js";

// ── aging ────────────────────────────────────────────────────────
export const agingInput = z.object({}).strict();

const agingBucket = z.object({
  bucket: z.enum(["0-30", "31-60", "61-90", "90+"]),
  customerCount: z.number().int().nonnegative(),
  outstandingPaise: moneyPaise,
  invoiceCount: z.number().int().nonnegative(),
});

const agingByCustomerRow = z.object({
  customerId: uuid,
  customerName: z.string(),
  outstandingPaise: moneyPaise,
  invoiceCount: z.number().int().nonnegative(),
  oldestDays: z.number().int().nonnegative(),
  /**
   * The single oldest open invoice's identity + lifecycle dates.
   * Drives the case-card lifecycle timeline on `/collections` so dates
   * shown to the operator are real, not derived from payment-term
   * assumptions. Always present — the aging query only emits a customer
   * row when at least one open invoice exists.
   */
  oldestInvoice: z.object({
    id: uuid,
    /** Document number once issued; never empty in this surface because
     *  the aging query filters to issued/partially_paid/overdue. */
    docNumber: z.string(),
    issuedOn: isoDate,
    dueOn: isoDate,
  }),
  openInvoices: z.array(
    z.object({
      id: uuid,
      docNumber: z.string(),
      issuedOn: isoDate,
      dueOn: isoDate,
      outstandingPaise: moneyPaise,
      ageDays: z.number().int().nonnegative(),
    }),
  ),
});

export const agingOutput = z.object({
  buckets: z.array(agingBucket),
  byCustomer: z.array(agingByCustomerRow),
  totalOutstandingPaise: moneyPaise,
});

// ── customerStatement ───────────────────────────────────────────
export const customerStatementInput = z.object({
  customerId: uuid,
  dateFrom: isoDate,
  dateTo: isoDate,
});

const statementRow = z.object({
  kind: z.enum(["opening", "invoice", "receipt", "credit-note"]),
  date: isoDate,
  reference: z.string(),
  description: z.string(),
  /** Signed paise (invoice positive, receipt/CN negative). String to carry sign. */
  signedPaise: z.string(),
  runningBalancePaise: z.string(),
});

export const customerStatementOutput = z.object({
  customer: z.object({
    id: uuid,
    name: z.string(),
    gstin: z.string().nullable(),
  }),
  dateFrom: isoDate,
  dateTo: isoDate,
  openingBalancePaise: z.string(),
  closingBalancePaise: z.string(),
  rows: z.array(statementRow),
});

// ── customerConcentration ───────────────────────────────────────
export const customerConcentrationInput = z.object({
  limit: z.number().int().min(1).max(100).default(20),
});

const concentrationRow = z.object({
  customerId: uuid,
  customerName: z.string(),
  outstandingPaise: moneyPaise,
  /** Basis-point integer (1234 = 12.34%). */
  sharePercentBps: z.number().int().nonnegative(),
  invoiceCount: z.number().int().nonnegative(),
  oldestDays: z.number().int().nonnegative(),
});

export const customerConcentrationOutput = z.object({
  totalOutstandingPaise: moneyPaise,
  rows: z.array(concentrationRow),
});

// ── invoiceActivityFeed ─────────────────────────────────────────
/**
 * Server-paginated activity timeline for one invoice. The SPA uses
 * `useInfiniteQuery` with `getNextPageParam` returning `nextCursor`;
 * cursor wire shape is `${at-iso}|${row-id}`, decoded by the router.
 */
export const invoiceActivityFeedInput = z.object({
  invoiceId: uuid,
  /** Page size — default 10 matches the "Load more" cadence on the SPA. */
  limit: z.number().int().min(1).max(100).default(10),
  /** Echo the previous response's `nextCursor`. Omit for the first page. */
  cursor,
});

const activityEntry = z.object({
  id: z.string(),
  // `{ offset: true }` accepts `+00:00` offsets in addition to `Z`. The
  // postgres-side `to_json(at)` emits offset form; default `.datetime()`
  // only accepts Z-suffix and would reject the value.
  at: z.string().datetime({ offset: true }),
  actorType: z.string(),
  actorName: z.string().nullable(),
  entityType: z.string(),
  verb: z.string(),
  metadata: z.record(z.string(), z.unknown()),
});

export const invoiceActivityFeedOutput = z.object({
  invoiceId: uuid,
  entries: z.array(activityEntry),
  nextCursor: cursor,
});

// ── Reports engine (ADR-0036, ADR-0037 retired) ───────────────────────
// Config-driven reports running through `@flint/reports-engine`. V1 ships
// a single Employee List report; the schemas below are written generically
// so new reports drop in without contract churn.

/** One value a viewer-supplied parameter can take. */
const parameterValue = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
  z.array(z.string()),
]);

/** Summary shape for the landing-page report card. */
const reportDefinitionSummary = z.object({
  key: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  category: z.string(),
  isPublished: z.boolean(),
  parameters: z.array(z.unknown()),
  exportFormats: z.array(z.enum(["pdf", "excel", "csv"])),
});

// listDefinitions
export const listDefinitionsInput = z.object({}).strict();
export const listDefinitionsOutput = z.object({
  rows: z.array(reportDefinitionSummary),
});

// getDefinition
export const getDefinitionInput = z.object({
  key: z.string().min(1),
});
export const getDefinitionOutput = reportDefinitionSummary.extend({
  displayTemplate: z.string().nullable(),
  headerTemplate: z.string().nullable(),
  footerTemplate: z.string().nullable(),
  /** Who created the row — `ai` rows are editable from the chat. */
  createdByActorType: z.enum(["system", "user", "ai"]),
  /** Raw SQL from `data_sources[0].script` — seeds the SQL tab's
   *  editor when opening a report for editing. */
  sqlTemplate: z.string(),
  /** Full pipeline as stored on the row — used by the manual builder
   *  to hydrate every data-source step in edit mode. */
  dataSources: z.array(
    z.object({
      key: z.string(),
      name: z.string(),
      type: z.literal("sql"),
      order: z.number().int().nonnegative(),
      script: z.string(),
    }),
  ),
});

// execute
export const executeInput = z.object({
  key: z.string().min(1),
  parameterValues: z.record(parameterValue).default({}),
});
export const executeOutput = z.object({
  executionId: uuid,
  html: z.string(),
  rowCount: z.number().int().nonnegative(),
  timings: z.object({
    queryMs: z.number().int().nonnegative(),
    renderMs: z.number().int().nonnegative(),
    totalMs: z.number().int().nonnegative(),
  }),
  resultHash: z.string(),
});

// exportCsv
export const exportCsvInput = z.object({
  key: z.string().min(1),
  parameterValues: z.record(parameterValue).default({}),
});
export const exportCsvOutput = z.object({
  fileName: z.string(),
  csv: z.string(),
});

// exportPdf — PDF bytes returned as base64 over JSON.
// V1 keeps it synchronous; large reports may need an async S3 path later.
export const exportPdfInput = z.object({
  key: z.string().min(1),
  parameterValues: z.record(parameterValue).default({}),
});
export const exportPdfOutput = z.object({
  fileName: z.string(),
  /** Standard base64 of the PDF byte stream. */
  pdfBase64: z.string(),
});

// ── aiDraft ───────────────────────────────────────────────────────
// Step 2: LLM emits a schema-constrained JSON draft.
// Step 3: catalog grounds column names in Loomwell's real tables.
// Step 4: LLM ALSO emits `sqlTemplate` — the router validates it with
//         the reports-engine SQL guard, runs it read-only inside a
//         tenant-scoped tx, and overwrites `placeholderRows` with real
//         result rows before returning. `placeholderRows` stays as the
//         wire field name so the frontend needs no changes.
export const draftDefinition = z.object({
  title: z.string().min(1).max(120),
  subtitle: z.string().min(1).max(200),
  /**
   * 3–5 short bullets explaining what the report shows. Size guidance
   * lives in the system prompt (not the schema) because Bedrock's
   * JSON-Schema handler rejects `minItems`/`maxItems` on arrays.
   */
  description: z.array(z.string()),
  /** 4–6 column labels. Size guidance lives in the system prompt. */
  columns: z.array(z.string()),
  /**
   * Step 4: real rows from the executor when the LLM's `sqlTemplate`
   * validates + runs cleanly. Falls back to the LLM's placeholder
   * values if the SELECT-clause column count doesn't match `columns[]`
   * (a coherence check — labels must line up with cells).
   */
  placeholderRows: z.array(z.array(z.string())),
  /**
   * Single-SELECT SQL the LLM emits alongside the shape. The router
   * runs this through `@flint/reports-engine`'s validator + executor
   * inside a tenant-scoped tx; result rows replace `placeholderRows`
   * before the response is sent. Kept top-level (not nested in a
   * `dataSources[]` array) to sidestep Bedrock's untested arrays-of-
   * objects handling.
   */
  sqlTemplate: z.string(),
  /**
   * Optional custom Nunjucks display template the agent authors when
   * the user asks for layout beyond the default flat table (KPI
   * cards, summary tiles, sections). Server-rendered against the real
   * result rows for the chat preview; baked into
   * `report_definitions.display_template` on save. Absent = the
   * auto-generated table template.
   */
  displayTemplate: z.string().optional(),
  /**
   * Optional multi-step data-source pipeline. Present when the user
   * has edited the chat's SQL tab stepper (added / reordered / renamed
   * steps beyond the AI's single query). When null/absent, the server
   * flattens `sqlTemplate` into one implicit step (`key: "rows"`).
   * The first step's script mirrors `sqlTemplate` so the Preview tab
   * keeps working — see `use-report-authoring-chat.ts` for the sync.
   */
  dataSources: z
    .array(
      z.object({
        key: z.string(),
        name: z.string(),
        type: z.literal("sql"),
        order: z.number().int().nonnegative(),
        script: z.string(),
      }),
    )
    .nullable()
    .optional(),
});

// ── saveAiDraft (Step 6 · Phase 2 — chat draft → report_definitions) ─
// Persists the chat's active draft to `report_definitions` so it
// appears on `/reports` alongside the seeded rows. Uses `draft.title`
// as the report name, `draft.subtitle` as the description, category
// "general" (later work will add editable name/category/description via
// a save modal). The router verifies the SQL still executes cleanly
// before insert — a failed verify surfaces as `BAD_REQUEST` at the
// wire boundary.
export const saveAiDraftInput = z
  .object({
    /** The active draft snapshot from `state.currentDraft`. */
    draft: draftDefinition,
    /**
     * Optional override for the persisted data-source pipeline. When
     * present, the server writes these steps verbatim (each verified
     * standalone) instead of flattening `draft.sqlTemplate` into one
     * implicit step. Sent by the chat when the user has touched the
     * SQL tab's stepper (added or reordered steps).
     */
    dataSources: z
      .array(
        z.object({
          key: z.string().min(1),
          name: z.string().min(1),
          type: z.literal("sql"),
          order: z.number().int().nonnegative(),
          script: z.string().min(1),
        }),
      )
      .min(1)
      .optional(),
  })
  .strict();
export const saveAiDraftOutput = z.object({
  /** URL slug for the new row: /reports/{reportKey}. */
  reportKey: z.string(),
  /** Row count from the verify-on-save execution. */
  verifiedRowCount: z.number().int().nonnegative(),
});

// ── updateAiDraft (Step 8 — edit-and-save-back) ────────────────────
// Updates an already-saved AI report's SQL in place. Server verifies
// the new SQL, regenerates the auto display template, bumps `version`,
// writes `updated_at = now()`. Restricted to rows whose
// `created_by_actor_type = 'ai'` — seeded reports are protected.
export const updateAiDraftInput = z
  .object({
    /** Key of the report to update — the URL slug on /reports/<key>. */
    reportKey: z.string().min(1),
    /** New SQL to persist. Server re-verifies via the ad-hoc executor. */
    sqlTemplate: z.string().min(1),
    /**
     * Optional custom display template to persist alongside the SQL.
     * Absent = the server regenerates the auto table template from
     * the SQL's columns (pre-existing behaviour).
     */
    displayTemplate: z.string().optional(),
    /**
     * Optional override for the persisted data-source pipeline. When
     * present, the server writes these steps verbatim (each verified
     * standalone) instead of flattening `sqlTemplate` into one step.
     * Same fork as `saveAiDraftInput.dataSources`.
     */
    dataSources: z
      .array(
        z.object({
          key: z.string().min(1),
          name: z.string().min(1),
          type: z.literal("sql"),
          order: z.number().int().nonnegative(),
          script: z.string().min(1),
        }),
      )
      .min(1)
      .optional(),
  })
  .strict();
export const updateAiDraftOutput = z.object({
  reportKey: z.string(),
  /** Row count from the verify-on-update execution. */
  verifiedRowCount: z.number().int().nonnegative(),
  /** New version number after the bump. */
  version: z.number().int().positive(),
  /** New updated_at ISO timestamp. */
  updatedAt: z.string().datetime({ offset: true }),
});

// ── updateAiDraftTemplate (Step 8.3 — edit display template) ──────
// Updates an already-saved AI report's display_template in place. Server
// smoke-renders the new Nunjucks against empty rows before persisting;
// bad templates fail with a `BAD_REQUEST`. Bumps `version`, writes
// `updated_at = now()`. Restricted to `created_by_actor_type = 'ai'`.
export const updateAiDraftTemplateInput = z
  .object({
    reportKey: z.string().min(1),
    /** New Nunjucks HTML to persist as `display_template`. */
    displayTemplate: z.string().min(1),
  })
  .strict();
export const updateAiDraftTemplateOutput = z.object({
  reportKey: z.string(),
  version: z.number().int().positive(),
  updatedAt: z.string().datetime({ offset: true }),
});

// ── previewAutoTemplate (Step 8.2 — Template tab pre-save) ─────────
// Read-only query that runs verify-on-execute + the shared template
// builder without persisting anything. The chat's Template tab uses
// this when the report hasn't been saved yet so the user can preview
// the exact Nunjucks that Save/Create will bake into the DB row.
export const previewAutoTemplateInput = z
  .object({
    sqlTemplate: z.string().min(1),
    labels: z.array(z.string()),
    reportName: z.string().min(1).max(200),
  })
  .strict();
export const previewAutoTemplateOutput = z.object({
  template: z.string(),
  verifiedRowCount: z.number().int().nonnegative(),
  /** SQL column aliases from the SELECT clause — used as fallback
   *  labels for the Preview tab when the caller doesn't know the
   *  original display labels (e.g. edit-mode bootstrap). */
  columns: z.array(z.string()),
  /** Formatted cell strings (money → "₹12,340", null → "—", etc.),
   *  same shape the chat's Preview tab consumes. */
  rows: z.array(z.array(z.string())),
});

// ── derived TS types ─────────────────────────────────────────────
export type AgingInput = z.infer<typeof agingInput>;
export type AgingOutput = z.infer<typeof agingOutput>;
export type CustomerStatementInput = z.infer<typeof customerStatementInput>;
export type CustomerStatementOutput = z.infer<typeof customerStatementOutput>;
export type CustomerConcentrationInput = z.infer<typeof customerConcentrationInput>;
export type CustomerConcentrationOutput = z.infer<typeof customerConcentrationOutput>;
export type InvoiceActivityFeedInput = z.infer<typeof invoiceActivityFeedInput>;
export type InvoiceActivityFeedOutput = z.infer<typeof invoiceActivityFeedOutput>;
export type ListDefinitionsInput = z.infer<typeof listDefinitionsInput>;
export type ListDefinitionsOutput = z.infer<typeof listDefinitionsOutput>;
export type GetDefinitionInput = z.infer<typeof getDefinitionInput>;
export type GetDefinitionOutput = z.infer<typeof getDefinitionOutput>;
export type ExecuteInput = z.infer<typeof executeInput>;
export type ExecuteOutput = z.infer<typeof executeOutput>;
export type ExportCsvInput = z.infer<typeof exportCsvInput>;
export type ExportCsvOutput = z.infer<typeof exportCsvOutput>;
export type ExportPdfInput = z.infer<typeof exportPdfInput>;
export type ExportPdfOutput = z.infer<typeof exportPdfOutput>;
export type DraftDefinition = z.infer<typeof draftDefinition>;
export type SaveAiDraftInput = z.infer<typeof saveAiDraftInput>;
export type SaveAiDraftOutput = z.infer<typeof saveAiDraftOutput>;
export type UpdateAiDraftInput = z.infer<typeof updateAiDraftInput>;
export type UpdateAiDraftOutput = z.infer<typeof updateAiDraftOutput>;
export type UpdateAiDraftTemplateInput = z.infer<typeof updateAiDraftTemplateInput>;
export type UpdateAiDraftTemplateOutput = z.infer<typeof updateAiDraftTemplateOutput>;
export type PreviewAutoTemplateInput = z.infer<typeof previewAutoTemplateInput>;
export type PreviewAutoTemplateOutput = z.infer<typeof previewAutoTemplateOutput>;
